import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import { formatApiError } from "@/components/auth/permissionProtectedRouteHelpers";
import {
  isOutageRelatedAccessFailure,
  useApiHealthRecovery,
} from "@/components/ui/ApiHealthRecoveryContext";
import { ADMIN_PERMISSION_KEYS } from "@/routing/adminPermissionKeys";
import {
  useGetAdminV2MeQuery,
  useGetAdminV2RevenueAccessContextQuery,
} from "@/store/api/baseApi";
import type { AdminV2RevenueMenuVisibility } from "@/types/adminV2";
import { resolveAdminRevenueAccessScope } from "@/utils/adminRevenueAccessScope";
import {
  getE2eBypassMenuVisibility,
  getE2eBypassPermissionKeys,
  isAdminE2eBypassEnabled,
  isAdminV2FoundationsEnabled,
} from "@/utils/featureFlags";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";

export interface PageLayoutShellAccess {
  adminV2FoundationsEnabled: boolean;
  permissionKeys: Set<string> | undefined;
  effectiveMenuVisibility: Partial<AdminV2RevenueMenuVisibility> | undefined;
  hasShellAccessDataError: boolean;
  suppressOutageAccessBanner: boolean;
  shellAccessCorrelationId: string | null;
  refetchShellAccessData: () => void;
}

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "usePageLayoutShellAccess",
);

const serializePermissionKeys = (
  permissionKeys?: Iterable<string>,
): string | null => {
  if (!permissionKeys) {
    return null;
  }

  return [...permissionKeys]
    .sort((left, right) => left.localeCompare(right))
    .join("|");
};

const arePermissionSetsEqual = (
  left?: Set<string>,
  right?: Set<string>,
): boolean => serializePermissionKeys(left) === serializePermissionKeys(right);

const serializeMenuVisibility = (
  menuVisibility?: Partial<AdminV2RevenueMenuVisibility>,
): string | null => {
  if (!menuVisibility) {
    return null;
  }

  return JSON.stringify({
    learning: menuVisibility.learning ?? null,
    revenue: menuVisibility.revenue ?? null,
    sales: menuVisibility.sales ?? null,
  });
};

const areMenuVisibilityEqual = (
  left?: Partial<AdminV2RevenueMenuVisibility>,
  right?: Partial<AdminV2RevenueMenuVisibility>,
): boolean => serializeMenuVisibility(left) === serializeMenuVisibility(right);

export const usePageLayoutShellAccess = (): PageLayoutShellAccess => {
  const location = useLocation();
  const { isDown: healthDown, recoveryGeneration } = useApiHealthRecovery();
  const adminV2FoundationsEnabled = isAdminV2FoundationsEnabled();
  const adminE2eBypassEnabled = isAdminE2eBypassEnabled();
  const revenueAccessScope = useMemo(
    () => resolveAdminRevenueAccessScope(location.search),
    [location.search],
  );
  const {
    data: adminIdentity,
    isError: isAdminIdentityError,
    isFetching: isAdminIdentityFetching,
    error: adminIdentityError,
    refetch: refetchAdminIdentity,
  } = useGetAdminV2MeQuery(undefined, {
    skip: !adminV2FoundationsEnabled || adminE2eBypassEnabled,
  });
  const {
    data: revenueAccessData,
    isLoading: isRevenueAccessLoading,
    isFetching: isRevenueAccessFetching,
    isError: isRevenueAccessError,
    error: revenueAccessError,
    refetch: refetchRevenueAccess,
  } = useGetAdminV2RevenueAccessContextQuery(revenueAccessScope, {
    skip: !adminV2FoundationsEnabled || adminE2eBypassEnabled,
  });

  const identityAccessError = useMemo(
    () => formatApiError(adminIdentityError),
    [adminIdentityError],
  );
  const revenueAccessErrorMessage = useMemo(
    () => formatApiError(revenueAccessError),
    [revenueAccessError],
  );
  const adminPermissionKeys = adminIdentity?.permissionKeys;
  const [lastKnownPermissionKeys, setLastKnownPermissionKeys] = useState<
    Set<string> | undefined
  >(undefined);
  const livePermissionKeysSignature = useMemo(
    () => serializePermissionKeys(adminPermissionKeys),
    [adminPermissionKeys],
  );
  const livePermissionKeys = useMemo(
    () =>
      adminPermissionKeys ? new Set<string>(adminPermissionKeys) : undefined,
    [adminPermissionKeys],
  );
  const stableLivePermissionKeys = useMemo(() => {
    if (!livePermissionKeys) {
      return undefined;
    }

    if (arePermissionSetsEqual(lastKnownPermissionKeys, livePermissionKeys)) {
      return lastKnownPermissionKeys;
    }

    return livePermissionKeys;
  }, [lastKnownPermissionKeys, livePermissionKeys]);

  useEffect(() => {
    if (!stableLivePermissionKeys) {
      return;
    }

    let isActive = true;
    queueMicrotask(() => {
      if (!isActive) {
        return;
      }

      // Remember the last good access snapshot without forcing same-render churn. When the
      // semantic permission set is unchanged, keep the old Set reference so shell consumers
      // and logs do not treat refetch identity churn as a real access change.
      setLastKnownPermissionKeys((previousPermissionKeys) => {
        if (
          arePermissionSetsEqual(
            previousPermissionKeys,
            stableLivePermissionKeys,
          )
        ) {
          return previousPermissionKeys;
        }

        return stableLivePermissionKeys;
      });
    });

    return () => {
      isActive = false;
    };
  }, [stableLivePermissionKeys]);

  const permissionKeys = useMemo(() => {
    if (adminE2eBypassEnabled) {
      const bypassPermissionKeys = getE2eBypassPermissionKeys() ?? [
        ...ADMIN_PERMISSION_KEYS,
      ];
      return new Set<string>(bypassPermissionKeys);
    }

    if (stableLivePermissionKeys) {
      return stableLivePermissionKeys;
    }

    if (isAdminIdentityError) {
      return lastKnownPermissionKeys;
    }

    return undefined;
  }, [
    adminE2eBypassEnabled,
    isAdminIdentityError,
    lastKnownPermissionKeys,
    stableLivePermissionKeys,
  ]);
  const [lastKnownMenuVisibility, setLastKnownMenuVisibility] = useState<
    Partial<AdminV2RevenueMenuVisibility> | undefined
  >(undefined);
  const liveRevenueMenuVisibility = revenueAccessData?.menuVisibility;
  const liveMenuVisibilitySignature = useMemo(
    () => serializeMenuVisibility(liveRevenueMenuVisibility),
    [liveRevenueMenuVisibility],
  );
  const liveMenuVisibility = useMemo(
    () => liveRevenueMenuVisibility,
    [liveRevenueMenuVisibility],
  );
  const stableLiveMenuVisibility = useMemo(() => {
    if (!liveMenuVisibility) {
      return undefined;
    }

    if (areMenuVisibilityEqual(lastKnownMenuVisibility, liveMenuVisibility)) {
      return lastKnownMenuVisibility;
    }

    return liveMenuVisibility;
  }, [lastKnownMenuVisibility, liveMenuVisibility]);

  useEffect(() => {
    if (!stableLiveMenuVisibility) {
      return;
    }

    let isActive = true;
    queueMicrotask(() => {
      if (!isActive) {
        return;
      }

      setLastKnownMenuVisibility((previousMenuVisibility) => {
        if (
          areMenuVisibilityEqual(
            previousMenuVisibility,
            stableLiveMenuVisibility,
          )
        ) {
          return previousMenuVisibility;
        }

        return stableLiveMenuVisibility;
      });
    });

    return () => {
      isActive = false;
    };
  }, [stableLiveMenuVisibility]);

  const effectiveMenuVisibility = useMemo<
    Partial<AdminV2RevenueMenuVisibility> | undefined
  >(() => {
    if (adminE2eBypassEnabled) {
      return (
        getE2eBypassMenuVisibility() ?? {
          revenue: true,
          learning: true,
          sales: true,
        }
      );
    }

    if (stableLiveMenuVisibility) {
      return stableLiveMenuVisibility;
    }

    // Keep nav visibility stable during transient refetch/loading windows
    // and brief backend outages so the shell stays usable in degraded mode.
    if (
      isRevenueAccessLoading ||
      isRevenueAccessFetching ||
      isRevenueAccessError
    ) {
      return lastKnownMenuVisibility;
    }

    return undefined;
  }, [
    adminE2eBypassEnabled,
    isRevenueAccessError,
    isRevenueAccessFetching,
    isRevenueAccessLoading,
    lastKnownMenuVisibility,
    stableLiveMenuVisibility,
  ]);

  const hasShellAccessDataError =
    adminV2FoundationsEnabled &&
    !adminE2eBypassEnabled &&
    (isAdminIdentityError || isRevenueAccessError);
  // CRITICAL PD-174 P70: while the backend is down, one outage notice owns
  // transport/server failures. A separate denial or rate limit stays visible.
  // https://www.rfc-editor.org/rfc/rfc9110#section-15.5.5
  const suppressOutageAccessBanner = Boolean(
    healthDown &&
    hasShellAccessDataError &&
    (!isAdminIdentityError ||
      isOutageRelatedAccessFailure(adminIdentityError)) &&
    (!isRevenueAccessError || isOutageRelatedAccessFailure(revenueAccessError)),
  );
  const recoveryPending = useRef({
    generation: recoveryGeneration,
    identity: false,
    revenue: false,
  });
  useEffect(() => {
    if (recoveryPending.current.generation !== recoveryGeneration) {
      recoveryPending.current = {
        generation: recoveryGeneration,
        identity: true,
        revenue: true,
      };
    }
    if (healthDown || !adminV2FoundationsEnabled || adminE2eBypassEnabled)
      return;
    // A recovery can arrive while an old request is still in flight. Wait for
    // it to settle before one recheck, and never retry 429/denial responses.
    if (recoveryPending.current.identity && !isAdminIdentityFetching) {
      recoveryPending.current.identity = false;
      if (
        isAdminIdentityError &&
        isOutageRelatedAccessFailure(adminIdentityError)
      )
        void refetchAdminIdentity();
    }
    if (recoveryPending.current.revenue && !isRevenueAccessFetching) {
      recoveryPending.current.revenue = false;
      if (
        isRevenueAccessError &&
        isOutageRelatedAccessFailure(revenueAccessError)
      )
        void refetchRevenueAccess();
    }
  }, [
    adminE2eBypassEnabled,
    adminIdentityError,
    adminV2FoundationsEnabled,
    healthDown,
    isAdminIdentityError,
    isAdminIdentityFetching,
    isRevenueAccessError,
    isRevenueAccessFetching,
    recoveryGeneration,
    refetchAdminIdentity,
    refetchRevenueAccess,
    revenueAccessError,
  ]);
  const shellAccessCorrelationId =
    identityAccessError.correlation ??
    revenueAccessErrorMessage.correlation ??
    null;
  const permissionKeysSignature = useMemo(
    () => serializePermissionKeys(permissionKeys),
    [permissionKeys],
  );
  const effectiveMenuVisibilitySignature = useMemo(
    () => serializeMenuVisibility(effectiveMenuVisibility),
    [effectiveMenuVisibility],
  );
  const usingLastKnownMenuVisibility =
    lastKnownMenuVisibility !== undefined && liveMenuVisibility === undefined;
  const usingLastKnownPermissionKeys =
    lastKnownPermissionKeys !== undefined && livePermissionKeys === undefined;

  useEffect(() => {
    logger.debug("Shell access state resolved", {
      operation: "shell_access_state_resolved",
      pathname: location.pathname,
      revenueAccessScope,
      permissionKeyCount: permissionKeys?.size ?? null,
      permissionKeysSignature,
      hasLivePermissionKeys: livePermissionKeysSignature !== null,
      hasLiveMenuVisibility: liveMenuVisibilitySignature !== null,
      hasShellAccessDataError,
      shellAccessCorrelationId,
      usingLastKnownMenuVisibility,
      usingLastKnownPermissionKeys,
      effectiveMenuVisibility: effectiveMenuVisibilitySignature,
    });
  }, [
    effectiveMenuVisibilitySignature,
    hasShellAccessDataError,
    liveMenuVisibilitySignature,
    livePermissionKeysSignature,
    location.pathname,
    permissionKeys?.size,
    permissionKeysSignature,
    revenueAccessScope,
    shellAccessCorrelationId,
    usingLastKnownMenuVisibility,
    usingLastKnownPermissionKeys,
  ]);

  const refetchShellAccessData = () => {
    logger.info("Manual shell access refetch requested", {
      operation: "shell_access_refetch_manual",
      pathname: location.pathname,
      revenueAccessScope,
      shellAccessCorrelationId,
    });
    void refetchAdminIdentity();
    void refetchRevenueAccess();
  };

  return {
    adminV2FoundationsEnabled,
    permissionKeys,
    effectiveMenuVisibility,
    hasShellAccessDataError,
    suppressOutageAccessBanner,
    shellAccessCorrelationId,
    refetchShellAccessData,
  };
};
